import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCarrierOrg } from '../../hooks/useCarrierOrg';
import { useCarrierMembers } from '../../hooks/useCarrierMembers';
import { carrierApi } from '../../firebase/functionsService';
import { formatPhone } from '../../utils/phone';
import { CARRIER_ROLE_LABEL } from '../../utils/carrierRoles';
import { CreateMemberForm, InviteMemberForm } from './MemberForms';
import DriverProfileForm from './DriverProfileForm';
import { ui, chip } from './carrierStyles';

function MemberRow({ member, canRemove, canChangeRole, onRemove, onChangeRole, onEdit }) {
  const profile = member.driverProfile;
  return (
    <div style={{ ...ui.listRow, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div>
        <strong>{member.name || 'Uten navn'}</strong>{' '}
        <span style={chip(member.role === 'driver' ? 'info' : 'none')}>{CARRIER_ROLE_LABEL[member.role] ?? member.role}</span>{' '}
        {member.status === 'pending' && <span style={chip('soon')}>Venter på svar</span>}
        <div style={ui.hint}>{member.phone ? formatPhone(member.phone) : 'Ingen mobil registrert'}</div>
        {member.role === 'driver' && (
          <div style={ui.hint}>
            Klasser: {profile?.licenseClasses?.length ? profile.licenseClasses.join(', ') : 'ikke satt'} · Språk: {profile?.languages?.length ? profile.languages.join(', ') : 'ikke satt'}
          </div>
        )}
      </div>
      <div style={ui.row}>
        {canChangeRole && (
          <select aria-label={`Rolle for ${member.name}`} style={{ ...ui.input, height: 38, width: 'auto' }} value={member.role} onChange={e => onChangeRole(member, e.target.value)}>
            <option value="admin">{CARRIER_ROLE_LABEL.admin}</option>
            <option value="dispatcher">{CARRIER_ROLE_LABEL.dispatcher}</option>
          </select>
        )}
        {member.role === 'driver' && member.status === 'active' && (
          <button type="button" style={ui.btnSecondary} onClick={() => onEdit(member)}>Rediger profil</button>
        )}
        {canRemove && <button type="button" style={ui.btnDanger} onClick={() => onRemove(member)}>Fjern</button>}
      </div>
    </div>
  );
}

export default function CarrierDrivers() {
  const { currentUser } = useAuth();
  const { loading: orgLoading, orgId, org, role, isStaff, isAdmin } = useCarrierOrg();
  const { members, loading } = useCarrierMembers(orgId);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  if (orgLoading) return null;
  if (!isStaff) return <p style={ui.hint}>Bare administratorer og dispatchere ser denne siden.</p>;

  async function act(fn, okMessage) {
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice(okMessage);
    } catch (err) {
      setError(err.message || 'Noe gikk galt');
    }
  }

  const remove = (m) => {
    if (!window.confirm(`Fjerne ${m.name || 'medlemmet'} fra firmaet?`)) return;
    act(() => carrierApi.removeCarrierMember({ orgId, targetUid: m.id }), `${m.name || 'Medlemmet'} er fjernet.`);
  };
  const changeRole = (m, newRole) => act(
    () => carrierApi.setCarrierMemberRole({ orgId, targetUid: m.id, role: newRole }),
    `${m.name || 'Medlemmet'} er nå ${CARRIER_ROLE_LABEL[newRole].toLowerCase()}.`,
  );

  const isOwnerRow = (m) => m.id === org?.ownerUid;
  const isSelf = (m) => m.id === currentUser?.uid;

  return (
    <>
      <h1 style={ui.h1}>Sjåfører og ansatte</h1>
      {notice && <p role="status" style={ui.ok}>{notice}</p>}
      {error && <p role="alert" style={ui.error}>{error}</p>}

      <div style={ui.card}>
        {loading && <p style={ui.hint}>Laster…</p>}
        {!loading && members.length === 0 && <p style={ui.hint}>Ingen medlemmer ennå.</p>}
        {members.map(m => (
          <div key={m.id}>
            <MemberRow
              member={m}
              canRemove={isAdmin && !isOwnerRow(m) && !isSelf(m)}
              canChangeRole={isAdmin && !isOwnerRow(m) && !isSelf(m) && m.role !== 'driver' && m.status === 'active'}
              onRemove={remove}
              onChangeRole={changeRole}
              onEdit={setEditing}
            />
            {editing?.id === m.id && (
              <div style={{ padding: '8px 0 16px' }}>
                <DriverProfileForm orgId={orgId} member={m} onDone={() => { setEditing(null); setNotice('Profilen er lagret.'); }} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={ui.card}>
        <h2 style={ui.h2}>Opprett sjåfør{isAdmin ? ' eller dispatcher' : ''}</h2>
        <CreateMemberForm orgId={orgId} callerRole={role} onDone={(message) => { setError(''); setNotice(message); }} />
      </div>

      <div style={ui.card}>
        <h2 style={ui.h2}>Inviter en som allerede har Tippi</h2>
        <InviteMemberForm orgId={orgId} callerRole={role} onDone={(message) => { setError(''); setNotice(message); }} />
      </div>
    </>
  );
}
